import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";

const faqs = [
  {
    question: "What services do you offer?",
    answer: (
      <>
        We offer{" "}
        <Link
          to="/services/website-development"
          className="text-primary hover:underline"
        >
          website development
        </Link>
        ,{" "}
        <Link
          to="/services/seo-services"
          className="text-primary hover:underline"
        >
          SEO services
        </Link>
        ,{" "}
        <Link
          to="/services/social-media-marketing"
          className="text-primary hover:underline"
        >
          social media marketing
        </Link>
        ,{" "}
        <Link
          to="/services/custom-tool-creation"
          className="text-primary hover:underline"
        >
          custom tool creation
        </Link>
        , and{" "}
        <Link
          to="/services/maintenance-support"
          className="text-primary hover:underline"
        >
          maintenance & support
        </Link>
        .
      </>
    ),
    schemaAnswer:
      "We offer website development, SEO services, social media marketing, custom tool creation, and maintenance & support.",
  },
  {
    question: "Do you only work in Vancouver?",
    answer:
      "We are based in Vancouver, but we work with businesses across Canada.",
  },
  {
    question: "How long does a website take?",
    answer:
      "Most business websites take around 4 to 8 weeks, depending on scope and functionality.",
  },
  {
    question: "Is SEO included in a website build?",
    answer:
      "Yes. SEO foundations, structure, and performance are built into the website from the start.",
  },
  {
    question: "Can you improve an existing website?",
    answer:
      "Yes. We can improve structure, performance, SEO, and conversion without rebuilding everything.",
  },
  {
    question: "What industries do you work with?",
    answer:
      "We have strong experience in insurance and automotive, and we work with businesses that need better visibility, trust, and digital systems.",
  },
];

export const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const toggleIndex = (index) => {
    setOpenIndex((prev) => (prev === index ? null : index));
  };

  return (
    <section className="pt-8 sm:pt-10 lg:pt-12 pb-12 sm:pb-14 lg:pb-16 bg-muted/30 relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute top-0 left-0 right-0 h-1/2 bg-gradient-to-b from-muted/30 to-transparent pointer-events-none" />

      <div className="container-custom relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-8 sm:mb-12"
        >
          <span className="text-sm font-medium text-primary uppercase tracking-wider">
            Frequently Asked Questions
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-foreground mt-3 mb-4 sm:mb-6">
            What Businesses Usually Ask{" "}
            <span className="gradient-text">First</span>
          </h2>
        </motion.div>

        {/* FAQ List */}
        <div className="max-w-3xl mx-auto space-y-3 sm:space-y-4">
          {faqs.map((faq, index) => (
            <motion.div
              key={faq.question}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.04 }}
              className="bg-card rounded-xl border border-border overflow-hidden"
            >
              <button
                type="button"
                onClick={() => toggleIndex(index)}
                className="w-full flex items-start sm:items-center justify-between gap-4 p-4 sm:p-6 text-left hover:bg-accent/50 transition-colors"
              >
                <span className="font-semibold text-foreground leading-snug">
                  {faq.question}
                </span>
                <ChevronDown
                  className={`h-5 w-5 text-muted-foreground transition-transform duration-300 flex-shrink-0 mt-0.5 sm:mt-0 ${
                    openIndex === index ? "rotate-180" : ""
                  }`}
                />
              </button>

              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.28 }}
                    className="overflow-hidden"
                  >
                    <div className="px-4 sm:px-6 pb-4 sm:pb-6 text-muted-foreground leading-relaxed text-sm sm:text-base">
                      {faq.answer}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>

        {/* FAQ Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map((faq) => ({
              "@type": "Question",
              name: faq.question,
              acceptedAnswer: {
                "@type": "Answer",
                text:
                  typeof faq.answer === "string" ? faq.answer : faq.schemaAnswer,
              },
            })),
          })}
        </script>
      </div>
    </section>
  );
};